import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Calendar, ChefHat, MapPin, Clock, Users, Euro, 
  Phone, FileText, ChevronLeft, MessageSquare, Loader2, Star, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { token, isChef, user } = useAuth();
  const { t } = useLanguage();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const authToken = token || localStorage.getItem('token');
        const [bookingRes, invoiceRes] = await Promise.all([
          fetch(`${API_URL}/api/bookings/${bookingId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
          }),
          fetch(`${API_URL}/api/bookings/${bookingId}/invoice`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
          }).catch(() => null)
        ]);

        if (bookingRes.ok) {
          const data = await bookingRes.json();
          setBooking(data);
        } else {
          navigate('/bookings');
        }
        
        if (invoiceRes && invoiceRes.ok) {
          const invoiceData = await invoiceRes.json();
          setInvoice(invoiceData);
        }
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, token, navigate]);

  const handleGenerateInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${bookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
        setInvoiceOpen(true);
        toast.success(t('invoice.generated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          booking_id: bookingId,
          origin_url: window.location.origin,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du paiement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    setStatusLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        toast.success(
          status === 'confirmed' ? 'Réservation confirmée !' :
          status === 'completed' ? 'Mission terminée !' :
          'Réservation annulée'
        );
        // Refresh booking data
        const bookingRes = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });
        if (bookingRes.ok) {
          const data = await bookingRes.json();
          setBooking(data);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleReview = async () => {
    setReviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          booking_id: bookingId,
          rating,
          comment,
        }),
      });

      if (response.ok) {
        toast.success('Avis publié ! Merci pour votre retour.');
        setReviewOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'cancelled') return <Badge variant="destructive">Annulée</Badge>;
    if (status === 'completed') return <Badge variant="outline">Terminée</Badge>;
    if (paymentStatus === 'paid') return <Badge className="bg-success">Payée</Badge>;
    if (status === 'confirmed') return <Badge>Confirmée</Badge>;
    return <Badge variant="secondary">En attente</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="booking-detail-page">
      <div className="container-app max-w-4xl">
        {/* Back button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/bookings')}
          data-testid="back-to-bookings"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour aux réservations
        </Button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold">
                Réservation #{booking.booking_id.slice(-8)}
              </h1>
              {getStatusBadge(booking.status, booking.payment_status)}
            </div>
            <p className="text-muted-foreground">
              Créée le {new Date(booking.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Chef/Client Info */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <ChefHat className="w-5 h-5" />
                  {isChef ? 'Client' : 'Chef'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-lg">
                      {isChef ? booking.client_name : booking.chef_name}
                    </p>
                    <p className="text-muted-foreground">{booking.client_email}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/messages')}
                    data-testid="message-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Event Details */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading">Détails de l'événement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{booking.date}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Horaire</p>
                      <p className="font-medium">{booking.start_time} • {booking.duration_hours}h</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Convives</p>
                      <p className="font-medium">{booking.guests} personnes</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Téléphone</p>
                      <p className="font-medium">{booking.phone}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Adresse</p>
                    <p className="font-medium">{booking.address}</p>
                  </div>
                </div>

                {booking.menu_notes && (
                  <>
                    <Separator />
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Notes pour le menu</p>
                        <p className="font-medium">{booking.menu_notes}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Actions & Payment */}
          <div className="space-y-6">
            {/* Price Summary */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  {t('booking.total')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <p className="text-4xl font-heading font-bold text-accent" data-testid="booking-detail-total">
                    {booking.total_price?.toFixed(2)}€
                  </p>
                  <p className="text-muted-foreground text-sm mt-1">
                    {booking.duration_hours}h {t('invoice.serviceDuration').toLowerCase()}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Section */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {t('invoice.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {invoice ? (
                  <>
                    <Badge className="bg-success" data-testid="invoice-sent-badge">{t('invoice.sent')}</Badge>
                    <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" data-testid="view-invoice-btn">
                          <Receipt className="w-4 h-4 mr-2" />
                          {t('invoice.view')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="font-heading">{t('invoice.title')} - {invoice.invoice_id}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4" data-testid="invoice-detail">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">{t('invoice.invoiceNumber')}</p>
                              <p className="font-medium">{invoice.invoice_id}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">{t('invoice.invoiceDate')}</p>
                              <p className="font-medium">{new Date(invoice.created_at).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">{t('invoice.billTo')}</p>
                              <p className="font-medium">{invoice.client_name}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">{t('invoice.serviceBy')}</p>
                              <p className="font-medium">{invoice.provider_name}</p>
                            </div>
                          </div>
                          <Separator />
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{t('invoice.providerRate')}</span>
                              <span>{invoice.hourly_rate?.toFixed(2)}€/h</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{t('invoice.serviceDuration')}</span>
                              <span>{invoice.duration_hours}h</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{t('invoice.subtotal')}</span>
                              <span>{invoice.subtotal?.toFixed(2)}€</span>
                            </div>
                            <div className="flex justify-between text-sm text-accent font-medium">
                              <span>{t('invoice.commission')} ({(invoice.commission_rate * 100).toFixed(0)}%)</span>
                              <span>{invoice.commission?.toFixed(2)}€</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between font-bold text-lg">
                              <span>{t('invoice.totalCharged')}</span>
                              <span data-testid="invoice-total">{invoice.total_price?.toFixed(2)}€</span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                ) : (
                  <>
                    {isChef ? (
                      <Button 
                        className="w-full" 
                        onClick={handleGenerateInvoice}
                        disabled={invoiceLoading}
                        data-testid="generate-invoice-btn"
                      >
                        <Receipt className="w-4 h-4 mr-2" />
                        {invoiceLoading ? t('invoice.generating') : t('invoice.generate')}
                      </Button>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center" data-testid="no-invoice-msg">
                        {t('invoice.noInvoice')}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="font-heading">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Client actions */}
                {!isChef && (booking.status === 'confirmed' || booking.status === 'pending') && booking.payment_status !== 'paid' && (
                  <Button
                    className="btn-primary w-full"
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    data-testid="pay-btn"
                  >
                    {paymentLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('booking.payNow')
                    )}
                  </Button>
                )}

                {!isChef && booking.status === 'completed' && (
                  <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full" variant="outline" data-testid="review-btn">
                        <Star className="w-4 h-4 mr-2" />
                        Laisser un avis
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-heading">Votre avis</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label className="mb-2 block">Note</Label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setRating(star)}
                                className="focus:outline-none"
                                data-testid={`star-${star}`}
                              >
                                <Star
                                  className={`w-8 h-8 transition-colors ${
                                    star <= rating
                                      ? 'fill-amber-400 text-amber-400'
                                      : 'text-muted-foreground/30'
                                  }`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="mb-2 block">Commentaire</Label>
                          <Textarea
                            placeholder="Partagez votre expérience..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            data-testid="review-comment"
                          />
                        </div>
                        <Button
                          className="btn-primary w-full"
                          onClick={handleReview}
                          disabled={reviewLoading}
                          data-testid="submit-review-btn"
                        >
                          {reviewLoading ? 'Publication...' : 'Publier'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Chef actions */}
                {isChef && booking.status === 'pending' && (
                  <>
                    <Button
                      className="w-full bg-success hover:bg-success/90"
                      onClick={() => handleStatusUpdate('confirmed')}
                      disabled={statusLoading}
                      data-testid="confirm-btn"
                    >
                      Confirmer la réservation
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => handleStatusUpdate('cancelled')}
                      disabled={statusLoading}
                      data-testid="cancel-btn"
                    >
                      Refuser
                    </Button>
                  </>
                )}

                {isChef && booking.status === 'paid' && (
                  <Button
                    className="btn-primary w-full"
                    onClick={() => handleStatusUpdate('completed')}
                    disabled={statusLoading}
                    data-testid="complete-btn"
                  >
                    Marquer comme terminée
                  </Button>
                )}

                {/* Cancel for client */}
                {!isChef && ['pending', 'confirmed'].includes(booking.status) && (
                  <Button
                    variant="outline"
                    className="w-full text-destructive hover:text-destructive"
                    onClick={() => handleStatusUpdate('cancelled')}
                    disabled={statusLoading}
                    data-testid="client-cancel-btn"
                  >
                    Annuler la réservation
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
